import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Paper from "@mui/material/Paper";
import {
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  IconButton,
  Tooltip,
  FormControlLabel,
  Switch,
  Typography,
  Card,
  CardContent,
  CardActions,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { doc, onSnapshot, query, setDoc } from "firebase/firestore";
import { storage } from "../firebase";
import { propertyCollection } from "../utils/firestorePaths";
import { createMeta } from "../utils/auditFields";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { DataGrid } from "@mui/x-data-grid";
import PropertyContext from "../contexts/PropertyContext";

// Create modal to add item types
const AddItemTypesModal = ({
  onClose,
  categories,
  setCategories,
  editingCategory,
}) => {
  const [itemType, setItemType] = useState("");
  const [displayInDownRoomsList, setDisplayInDownRoomsList] = useState(true);
  const { selectedProperty, selectedClient } = useContext(PropertyContext);

  useEffect(() => {
    if (editingCategory) {
      setItemType(editingCategory.name || editingCategory);
      setDisplayInDownRoomsList(
        editingCategory.displayInDownRoomsList !== false
      );
    }
  }, [editingCategory]);

  const handleAdd = () => {
    if (!editingCategory) {
      setCategories([...categories, itemType]);
    }
    //add category to firestore
    const itemCatagoriesRef = propertyCollection(
      selectedClient,
      selectedProperty,
      "itemCatagories"
    );
    const itemCatagoriesDoc = doc(itemCatagoriesRef, itemType);
    setDoc(itemCatagoriesDoc, {
      name: itemType,
      displayInDownRoomsList: displayInDownRoomsList,
      ...createMeta(),
    });

    //cleanup
    setItemType("");
    setDisplayInDownRoomsList(true);
    onClose();
  };

  return (
    <Modal open={true} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "auto",
          minWidth: "30rem", // Adjust the width as needed
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 2,
          outline: "none",
        }}
      >
        <Paper elevation={3} style={{ padding: "20px" }}>
          <TextField
            label="Item Type/Category"
            value={itemType}
            onChange={(e) => setItemType(e.target.value)}
            fullWidth
            margin="normal"
          />
          <FormControlLabel
            control={
              <Switch
                checked={displayInDownRoomsList}
                onChange={(e) => setDisplayInDownRoomsList(e.target.checked)}
                color="primary"
              />
            }
            label="Display in Down Rooms List"
            sx={{ mt: 2, mb: 2 }}
          />
          {!editingCategory && (
            <List>
              {categories.map((category, index) => (
                <ListItem key={index}>
                  {typeof category === "object"
                    ? category.name || category.id
                    : category}
                </ListItem>
              ))}
            </List>
          )}
          <Box
            sx={{
              display: "flex",
              width: "100%",
              justifyContent: "space-between",
            }}
          >
            <Button
              variant="contained"
              color="secondary"
              onClick={onClose}
              style={{ marginBottom: "20px" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleAdd}
              style={{ marginBottom: "20px" }}
            >
              {editingCategory ? "Update Category" : "Add Category"}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Modal>
  );
};

// Create modal to add items
const AddItemsModal = ({ itemTypes, vendors, onClose, data }) => {
  const [vendor, setVendor] = useState("");
  const [itemID, setItemID] = useState("");
  const [itemDifficulty, setItemDifficulty] = useState("");
  const [itemName, setItemName] = useState("");
  const [itemType, setItemType] = useState("");
  const [itemGLCode, setItemGLCode] = useState("");
  const [itemCost, setItemCost] = useState("");
  const [itemResponsibility, setItemResponsibility] = useState(""); //housekeeping, maintenance
  const [itemSKU, setItemSKU] = useState("");
  const [loadedData, setLoadedData] = useState(false);
  const [sdsFile, setSdsFile] = useState(null);
  const [sdsURL, setSdsURL] = useState("");
  const [uploading, setUploading] = useState(false);

  const { selectedProperty, selectedClient } = useContext(PropertyContext);

  //   console.log(vendors);

  // Handle SDS file upload
  const handleSdsUpload = async (file) => {
    if (!file) return null;

    setUploading(true);
    try {
      const storageRef = ref(
        storage,
        `properties/${selectedProperty}/items/${itemName.replace(
          /\s/g,
          ""
        )}/sds_${Date.now()}.pdf`
      );
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      setUploading(false);
      return downloadURL;
    } catch (error) {
      console.error("Error uploading SDS:", error);
      alert("Error uploading SDS file. Please try again.");
      setUploading(false);
      return null;
    }
  };

  // Handle the item addition logic here
  const handleAdd = async () => {
    // Upload SDS if a new file was selected
    let finalSdsURL = sdsURL;
    if (sdsFile) {
      const uploadedURL = await handleSdsUpload(sdsFile);
      if (uploadedURL) {
        finalSdsURL = uploadedURL;
      }
    }

    // Add item to firestore or other sources
    const itemsRef = propertyCollection(
      selectedClient,
      selectedProperty,
      "items"
    );
    let itemsDoc = doc(itemsRef, `${itemName.replace(/\s/g, "")}`);

    // setDoc upserts (same doc id on create + edit); createMeta records the
    // last save author/time.
    setDoc(itemsDoc, {
      vendor: vendor,
      itemID: `itemCount${itemName.replace(/\s/g, "")}`,
      itemDifficulty: itemDifficulty,
      itemName: itemName,
      itemType: itemType,
      itemGLCode: itemGLCode,
      itemSKU: itemSKU,
      itemCost: itemCost,
      itemResponsibility: itemResponsibility,
      sdsURL: finalSdsURL || "",
      ...createMeta(),
    });

    // Cleanup
    onClose();
  };

  useEffect(() => {
    if (data === null) return;
    setLoadedData(true);
    console.log(data);
    const item = data.row;
    setVendor(item.vendor);
    setItemCost(item.itemCost);
    setItemDifficulty(item.itemDifficulty);
    setItemGLCode(item.itemGLCode);
    setItemSKU(item.itemSKU);
    setItemName(item.itemName);
    setItemResponsibility(item.itemResponsibility);
    setItemType(item.itemType);
    setSdsURL(item.sdsURL || "");
  }, [data]);

  return (
    <Modal open={true} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "auto",
          minWidth: "30rem",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 2,
          outline: "none",
        }}
      >
        <Paper elevation={3} style={{ padding: "20px" }}>
          <FormControl fullWidth margin="normal">
            <InputLabel>Vendor</InputLabel>
            <Select
              value={vendor}
              label="Vendor"
              onChange={(e) => setVendor(e.target.value)}
            >
              {vendors.map((vendor, index) => (
                <MenuItem key={index} value={vendor}>
                  {vendor}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <InputLabel>Item Category</InputLabel>
            <Select
              value={itemType}
              label="Item Category"
              onChange={(e) => setItemType(e.target.value)}
            >
              {itemTypes.map((type, index) => (
                <MenuItem
                  key={index}
                  value={typeof type === "object" ? type.name || type.id : type}
                >
                  {typeof type === "object" ? type.name || type.id : type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <InputLabel>Item Responsibility</InputLabel>
            <Select
              value={itemResponsibility}
              label="Item Responsibility"
              onChange={(e) => setItemResponsibility(e.target.value)}
            >
              <MenuItem value="Housekeeping">Housekeeping</MenuItem>
              <MenuItem value="Maintenance">Maintenance</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Item Difficulty"
            value={itemDifficulty}
            onChange={(e) => setItemDifficulty(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Item Name"
            value={itemName}
            onChange={(e) => setItemName(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Item SKU"
            value={itemSKU}
            onChange={(e) => setItemSKU(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Item GL Code"
            value={itemGLCode}
            onChange={(e) => setItemGLCode(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Item Cost"
            value={itemCost}
            onChange={(e) => setItemCost(e.target.value)}
            fullWidth
            margin="normal"
          />

          {/* SDS PDF Upload Section */}
          <Box sx={{ mt: 2, mb: 2 }}>
            <Button
              variant="outlined"
              component="label"
              fullWidth
              disabled={uploading}
            >
              {uploading
                ? "Uploading SDS..."
                : sdsFile
                ? `SDS: ${sdsFile.name}`
                : sdsURL
                ? "SDS Uploaded - Upload New"
                : "Upload SDS PDF (Optional)"}
              <input
                type="file"
                hidden
                accept="application/pdf"
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    setSdsFile(e.target.files[0]);
                  }
                }}
              />
            </Button>
            {sdsURL && !sdsFile && (
              <Box
                sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}
              >
                <PictureAsPdfIcon color="error" />
                <Button
                  size="small"
                  onClick={() => window.open(sdsURL, "_blank")}
                >
                  View Current SDS
                </Button>
              </Box>
            )}
          </Box>

          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
            <Button
              variant="contained"
              color="secondary"
              onClick={onClose}
              disabled={uploading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleAdd}
              disabled={uploading}
            >
              {uploading ? "Uploading..." : "Add Item"}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Modal>
  );
};

function AddItems() {
  const [loading, setLoading] = useState(true); // Assuming you have a loading state
  const [itemTypes, setItemTypes] = useState([]);
  const [vendors, setVendors] = useState([]); // Assuming you have a vendors state
  const [items, setItems] = useState([]); // Assuming you have an items state
  const [additemModal, setAddItemModal] = useState(false);
  const [addItemTypeModal, setAddItemTypeModal] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);

  const { selectedProperty, selectedClient } = useContext(PropertyContext);
  const navigate = useNavigate();

  console.log("rendered additems");

  useEffect(() => {
    // Fetch item types and vendors from firestore or other sources
    setLoading(true);
    //fetch vendors
    const vendorsRef = propertyCollection(
      selectedClient,
      selectedProperty,
      "Vendors"
    );
    const vendorsQuery = query(vendorsRef);
    const unsubscribeVendors = onSnapshot(vendorsQuery, (querySnapshot) => {
      const vendorsArray = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        vendorsArray.push(data.name);
      });
      setVendors(vendorsArray);
    });

    //fetch item types
    const itemCatagoriesRef = propertyCollection(
      selectedClient,
      selectedProperty,
      "itemCatagories"
    );
    const itemCatagoriesQuery = query(itemCatagoriesRef);
    const unsubscribeItemCatagories = onSnapshot(
      itemCatagoriesQuery,
      (querySnapshot) => {
        const itemCatagoriesArray = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          itemCatagoriesArray.push({
            id: doc.id,
            name: data.name || doc.id,
            displayInDownRoomsList: data.displayInDownRoomsList !== false, // default true for backwards compatibility
          });
        });
        setItemTypes(itemCatagoriesArray);
      }
    );

    //fetch Items
    const itemsRef = propertyCollection(
      selectedClient,
      selectedProperty,
      "items"
    );
    const itemsQuery = query(itemsRef);
    const unsubscribeItems = onSnapshot(itemsQuery, (querySnapshot) => {
      const itemsArray = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        itemsArray.push(data);
      });
      setItems(itemsArray);
    });

    setLoading(false);
    return () => {
      unsubscribeVendors();
      unsubscribeItemCatagories();
      unsubscribeItems();
    };
  }, []);
  const collumns = [
    { field: "itemName", headerName: "Name", flex: 1, editable: false },
    { field: "itemGLCode", headerName: "GL Code", flex: 1, editable: false },
    { field: "itemType", headerName: "Category", flex: 1, editable: false },
    {
      field: "itemResponsibility",
      headerName: "Responsibility",
      flex: 1,
      editable: false,
    },
    {
      field: "itemDifficulty",
      headerName: "Minutes Needed",
      flex: 1,
      editable: false,
    },
    { field: "vendor", headerName: "Vendor", flex: 1, editable: false },
    {
      field: "itemCost",
      headerName: "Estimated Cost",
      flex: 1,
      editable: false,
    },
    {
      field: "itemSKU",
      headerName: "SKU",
      flex: 1,
      editable: false,
    },
    {
      field: "sdsURL",
      headerName: "SDS",
      width: 80,
      editable: false,
      sortable: false,
      renderCell: (params) => {
        if (params.value) {
          return (
            <Tooltip title="View SDS PDF">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(params.value, "_blank");
                }}
              >
                <PictureAsPdfIcon color="error" />
              </IconButton>
            </Tooltip>
          );
        }
        return null;
      },
    },
  ];

  return (
    <div>
      {/* if loading, show mui circular indicator, else, show div */}
      {loading ? (
        <div>
          <CircularProgress />
        </div>
      ) : (
        <div style={{ maxWidth: "100%" }}>
          {/* Category Management Widget */}
          <Card
            sx={{
              mb: 3,
              bgcolor: "background.default",
              border: 1,
              borderColor: "divider",
            }}
          >
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <SettingsIcon sx={{ fontSize: 40, color: "primary.main" }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" gutterBottom>
                    Item Categories
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {itemTypes.length} categories configured •{" "}
                    {
                      itemTypes.filter(
                        (c) => c.displayInDownRoomsList !== false
                      ).length
                    }{" "}
                    visible in down rooms
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => navigate("/manage-categories")}
                  startIcon={<SettingsIcon />}
                >
                  Manage Categories
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Show your items here */}
          <Box sx={{ mb: 2 }}>
            <Button onClick={() => setAddItemModal(true)}>Add Item</Button>
          </Box>

          {/* Map your items here */}
          <DataGrid
            rows={items}
            columns={collumns}
            pageSize={10}
            rowsPerPageOptions={[10]}
            disableSelectionOnClick
            getRowId={(row) => row.itemID}
            onRowClick={(row) => {
              setModalData(row);
              setAddItemModal(true);
            }}
            sx={{ maxWidth: "100%" }}
          />
          {addItemTypeModal && (
            <AddItemTypesModal
              categories={itemTypes}
              setCategories={setItemTypes}
              editingCategory={editingCategory}
              onClose={() => {
                setAddItemTypeModal(false);
                setEditingCategory(null);
              }}
            />
          )}
          {additemModal && (
            <AddItemsModal
              itemTypes={itemTypes}
              vendors={vendors}
              onClose={() => {
                setAddItemModal(false);
                setModalData(null);
              }}
              data={modalData}
            />
          )}
        </div>
      )}
    </div>
  );
}

export default AddItems;
